/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { listVariantsForProduct } from '../../catalog/infra-api';
import type { ProductVariant } from '../../catalog/domain-api';

type Props = {
  productId: string;
  selectedVariantId: string | null;
  onSelect: (variantId: string | null, variantName: string | null) => void;
  disabled?: boolean;
};

function formatVariantLabel(variant: ProductVariant): string {
  if (variant.nameSuffix?.trim()) return variant.nameSuffix.trim();
  const values = Object.values(variant.attributes ?? {}).filter((v) => v?.trim());
  return values.join(' · ');
}

/**
 * #713: Picker de variantes para la creación de OC.
 * Se renderiza después de seleccionar un producto con variantes activas.
 * Permite elegir una variante específica o el parent (null).
 */
export function PurchaseVariantPicker({ productId, selectedVariantId, onSelect, disabled }: Props) {
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listVariantsForProduct(productId)
      .then((result) => {
        if (cancelled) return;
        const active = result.filter((v) => v.isActive);
        setVariants(active);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setVariants([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (loading || variants.length === 0) return null;

  return (
    <ModalFormField label="Variante">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Parent option */}
        <VariantChip
          label="Producto padre"
          selected={selectedVariantId == null}
          onPress={() => onSelect(null, null)}
          disabled={disabled}
        />
        {variants.map((v) => {
          const label = formatVariantLabel(v);
          return (
            <VariantChip
              key={v.id}
              label={label}
              selected={selectedVariantId === v.id}
              onPress={() => onSelect(v.id, label)}
              disabled={disabled}
            />
          );
        })}
      </ScrollView>
    </ModalFormField>
  );
}

function VariantChip({
  label,
  selected,
  onPress,
  disabled,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[styles.chip, selected && styles.chipSelected, disabled && styles.chipDisabled]}
    >
      <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flexGrow: 0,
  },
  scrollContent: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.border,
    backgroundColor: ticketColors.paper,
  },
  chipSelected: {
    borderColor: ticketColors.ink,
    backgroundColor: ticketColors.ink,
  },
  chipDisabled: {
    opacity: 0.5,
  },
  chipText: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink,
  },
  chipTextSelected: {
    color: ticketColors.paper,
  },
});
